<script>
import { onMount } from 'svelte';
import Alert from './Alert.svelte';
import Modal from './Modal.svelte';
import Fireworks from './Fireworks.svelte';
import { escape, br, autolink, autoselect } from '../libs/helpers';
import globals from '../globals';

let informData = null;
let alertData = null;
let confirmData = null;
let promptData = null;
let informRef = null;
let hovered = false;

function resolve(obj, value) {
  obj && obj.resolve(value);
}

function resolveIfTimedout(obj, value) {
  obj && obj.resolveIfTimedout(value);
}

// Type of success (green), info (blue), warning (yellow), danger (red)
export function inform(type, title, message) {
  if (informData) {
    clearTimeout(informData.timeout);
    informData = null;
  }

  if (!message) {
    message = title;
    title = '';
  }

  return new Promise(resolve => {
    let timeout = setTimeout(() => {
      timeout = 0;
      if (!hovered) resolve();
    }, 4000);

    informData = { type, title, message, resolve, timeout, resolveIfTimedout: () => {
      if (!timeout) {
        hovered = false;
        resolve();
      }
    }};
  }).then(() => {
    informData = null;
  });
}

export function alert(title, message, options = {}) {
  return new Promise(resolve => {
    alertData = { title, message, resolve, options };
  }).then(() => {
    alertData = null;
  });
}

export function confirm(title, message, options = {}) {
  if (typeof message !== 'string') {
    options = message || options;
    message = title;
    title = 'Confirm';
  }

  return new Promise(resolve => {
    confirmData = { title, message, options, resolve };
  }).then(answer => {
    confirmData = null;
    return answer;
  });
}

export function prompt(title, message, options = {}) {
  return new Promise(resolve => {
    promptData = { title, message, options, input: options.value || '', resolve };
  }).then(answer => {
    promptData = null;
    return answer;
  });
}
</script>

{#if informData}
<Alert type={informData.type} dismissible global bind:hovered on:close={() => resolve(informData)} on:unhovered={() => resolveIfTimedout(informData)} visible={informData}>
  {#if informData.title}<strong>{informData.title}</strong>{/if} {informData.message}
</Alert>
{/if}

{#if alertData}
<Modal title={alertData.title} size="small" on:close={() => resolve(alertData)} nofocus={alertData.options.noFocus}>
  <section>
    {#if alertData.options.fireworks}
    <Fireworks/>
    {/if}
    <p>{@html autolink(br(escape(alertData.message)), true)}</p>
  </section>
  <footer>
    <button class="btn primary" on:click={() => resolve(alertData)}>OK</button>
  </footer>
</Modal>
{/if}

{#if confirmData}
<Modal title={confirmData.title} size="small" on:close={() => resolve(confirmData, false)} nofocus={confirmData.options.noFocus}>
  <section>
    <p>{@html autolink(br(escape(confirmData.message)), true)}</p>
  </section>
  <footer>
    <button class="btn secondary" on:click={() => resolve(confirmData, false)}>{confirmData.options.yesNo ? 'No' : 'Cancel'}</button>
    <button class="btn primary" on:click={() => resolve(confirmData, true)}>{confirmData.options.yesNo ? 'Yes' : 'OK'}</button>
  </footer>
</Modal>
{/if}

{#if promptData}
<Modal title={promptData.title} size="small"  on:close={() => resolve(promptData, '')} nofocus={promptData.options.noFocus}>
  <section>
    <p>{@html autolink(br(escape(promptData.message)), true) }</p>
    <input type="text" class="form-control" placeholder={promptData.options.placeholder || '' } bind:value={promptData.input} on:keydown={event => event.key === 'Enter' && resolve(promptData, promptData.input)} use:autoselect>
  </section>
  <footer>
    <button class="btn secondary" on:click={() => resolve(promptData, '')}>Cancel</button>
    <button class="btn primary" on:click={() => resolve(promptData, promptData.input)}>OK</button>
  </footer>
</Modal>
{/if}
